/* eslint-disable @typescript-eslint/no-misused-promises */
// @ts-strict-ignore
import { JingleSession } from "../../libs/strophe/strophe.jingle.session";
import { stringifyError } from "../../utils/stringifyError";
import { LoggerService, SettingsService, XmppService } from "../core";
import { ContactService } from "../core/contact.service";
import { WebrtcConfigAndUtils } from "./webrtcConfigAndUtils";

/**
 * Interface containing all stats related to the audio stats for the sessions output-rtp (outflow) + input-rtp (inflow)
 */
export interface WebrtcStatisticsSsrc {
    type: string;
    direction: string;
    kind: string;
    ssrc: string;
    startedAt: string;
    duration?: number;
    measures?: number;
    lastMeasures?: number;
    codec?: string;
    originator?: string;
    rules?: any;
    audioLevel?: AudioLevelStats;
    mos?: MosLevelStats;

    //these values are removed before sending the ssrc to the server !

    //temp value of the codec ID so that we can map the id to the codec name
    codecId?: string;
    //temp objects where we store values to calculate delta
    audioStats?: any;
    packetStats?: any;

    //the current jitter in MS
    jitterMs?: any;

    //for outbound-rtp, to map the rtp to the mediaSourceId
    mediaSourceId?: string;

    //packet lot for both inbound & outbound RTP
    deltaPacketsLost?: number;
    totalPacketsLost?: number;
    deltaPackets?: number;
}

export interface AudioLevelStats {
    percentMuted: number;
    percentQuiet: number;
    percentBackground: number;
    percentActive: number;
    percentLoud: number;
}

export interface MosLevelStats {
    percentVeryHigh: number;
    percentHigh: number;
    percentMedium: number;
    percentLow: number;
    percentMediocre: number;
    mean: number;
}

export interface WebrtcStatisticsNetwork {
    /**
     * Type of interface usedCan be "wifi", "vpn", "ethernet", "unknown", "cellular"
     * From networkType in local-candidate report
     */
    networkType?: string;

    /**
     * Route used by the peer-connectionFormat: <local_address>: [type] (protocol) <-> <remote_address>: [type] (protocol)
     * Computed from statistics in local_candidate and remote_candidate depending on the localCandidateId and remoteCandidateId in the candidate-pair report in use (selectedCandidatePairId in tranport report or selected=true in candidate-pair report
     */
    candidatePair?: string;
}

/**
 * Object containing all stats related to the output-rtp (outflow)
 */
export interface WebrtcStatisticsOutboundAudio {
    /**
     * The total of packets sent from the beginning of this RTP stream.From packetsSent in outbound-rtp report
     */
    totalPacketsSent?: number;

    /**
     * The total of bytes sent from the beginning of this RTP stream.From bytesSent outbound-rtp report
     */
    totalBytesSent?: number;

    /**
     * The delta of the audio KB sent between the current and the last iteration
     */
    deltaAudioKBSent?: number;

    /**
     * The current encoder target in bits per second for this RTP stream.From targetBitrate in outbound-rtp report
     */
    targetBitrate?: number;

    /**
     * Represents the audio level of the media source detected from the input device
     */
    audioInputLevel?: number;

    /**
     * If exist, the value of the media identification-tag negotiated for this RTP streamFrom mid in inbound-rtp or outbound-rtp report
     */
    mid?: number;

    /**
     * Name of the inbound codec used (eg: "opus")
     * Computed from mimeType (remove "audio/") in the codec report associated to this RTP Stream (codecId from the inbound-rtp report)
     */
    codec?: string;

    /**
     * The total of audio energy received or sent from the beginning of this RTP stream.
     */
    totalAudioEnergy?: string;
}

/**
 * Object containing all stats related to the inbound-rtp (inflow)
 */
export interface WebrtcStatisticsInboundAudio {
    /**
     * The total of packets received from the beginning of this RTP stream.From packetsReceived in inbound-rtp report
     */
    totalPacketsReceived?: number;

    /**
     * The total of packets discarded from the beginning of this RTP stream.From packetsDiscarded in inbound-rtp report
     */
    totalPacketsReceivedDiscarded?: number;

    /**
     * The total of packets lost from the beginning of this RTP stream.From packetsLost in inbound-rtp report
     */
    totalPacketsLost?: number;

    /**
     * The total of bytes received from the beginning of this RTP stream.From bytesReceived inbound-rtp report
     */
    totalBytesReceived?: number;

    /**
     * The number of packets received for this RTP stream from the last measurement. Computed from packetsReceived in inbound-rtp report
     */
    deltaPacketsReceived?: number;

    /**
     * The number of KB received for this RTP stream since the last measurement.
     */
    deltaKBReceived?: number;

    /**
     * Mean of the round trip time during the last interval.Computed from totalRoundTripTime and roundTripTimeMeasurements in remote-outbound-rtp report
     */
    roundTripTimeMs?: number;

    /**
     * Mean of the jitter buffer delay during the last interval.Computed From jitterBufferDelay and jitterBufferEmittedCount in inbound-rtp report
     */
    jitterBufferMs?: number;

    /**
     * Value of the jitter for the current measurement.From jitter in inbound-rtp report
     */
    jitterMs?: number;

    /**
     * Mean for the last interval. Computed from the codec used, the absolute delay computed and the packets lost.
     */
    mos?: number;

    /**
     * Name of the inbound codec used (eg: "opus")
     * Computed from mimeType (remove "audio/") in the codec report associated to this RTP Stream (codecId from the inbound-rtp report)
     */
    codec?: string;

    /**
     * If exist, the value of the media identification-tag negotiated for this RTP streamFrom mid in inbound-rtp or outbound-rtp report
     */
    mid?: number;

    /**
     * The MEAN MOS value for the current session
     */
    meanMOS?: number;

    /**
     * Represents the audio level of the media source seen from the output device
     */
    audioOutputLevel?: number;

    /**
     * Represents the absolute delay computed with: absoluteDelay:number = 20 + RTT/2 + JitterBufferDelay where
     * 20 represents the packetization time (fixed value) in milliseconds
     * RTT as computed in Compute the Round Trip Time in milliseconds
     * JitterBufferDelay in milliseconds
     */
    absDelayMs?: number;

    /**
     * A value indicating the current quality of the incoming audio based on the MOS value; Values are 0, 1, 2; as 0 is bad, 3 is good
     */
    networkQualityValue?: number;

    /**
     * A SSRC Identifiant
     */
    ssrc?: string;

    /**
     * The total of audio energy received or sent from the beginning of this RTP stream.
     */
    totalAudioEnergy?: string;

    /**
     * Mean of the difference between the processing time and the buffer time during the last interval
     */
    processingMs?: number;

    /**
     * Mean of the playout delay during the last interval
     */
    playoutDelayMs?: number;

    /**
     * Counter. Inbound The total of FEC packets used from the beginning of this RTP stream.Computed from (fecPacketsReceived - fecPacketsDiscarded) (inbound-rtp report)
     */
    totalFecPacketsUsed?: number;

    /**
     * Counter. Inbound The total of packets pulled out of the Jitter Buffer from the beginning of this RTP stream.From jitterBufferEmittedCount (inbound-rtp report)
     */
    totalJitterBufferEmittedCount?: number;

    /**
     * Counter. Inbound The total of concealed samples from the beginning of this RTP stream.From concealedSamples (inbound-rtp report)
     */
    totalConcealedSamples?: number;

    /**
     * Counter. Inbound The total of samples received from the beginning of this RTP stream.From samplesReceived (inbound-rtp report)
     */
    totalSamplesReceived?: number;

    /**
     * Gauge (0..1). Inbound The average loudness for the last intervalComputed from Math.sqrt(totalAudioEnergy / totalSamplesDuration)  (inbound-rtp report). With totalSamplesDuration in seconds.
     */
    rms?: number;

    /**
     * Counter. Inbound The total of packets emitted for playout from the beginning of this RTP stream.From totalSamplesCount (media-playout report)
     */
    totalSamplesCount?: number;

    /**
     * Gauge (0..100%). Inbound The percentage of synthesized samples played for the last interval of this RTP streamComputed from synthesizedSamplesDuration and totalSamplesDuration (media-playout report)
     */
    synthesizedSamplesPlayed?: number;
}

/**
 * Interface containing all stats related to the audio stats for the sessions output-rtp (outflow) + input-rtp (inflow)
 */
export interface WebrtcStatisticsAudio {
    /**
     * Object containing all stats related to the output-rtp (outflow)
     */
    sent?: WebrtcStatisticsOutboundAudio;

    /**
     * Object containing all stats related to the inbound-rtp (inflow)
     */
    received?: WebrtcStatisticsInboundAudio;
}

/**
 * Interface containing all stats related to the OUTBOUND video stats for the sessions
 * NOTE The same interface is also related to the screen sharing, as in terms of WEBRTC it's a VIDEO stream
 */
export interface WebrtcStatisticsOutboundVideo {
    /**
     * The total of packets sent from the beginning of this RTP stream. From packetsSent in outbound-rtp report
     */
    totalPacketsSent?: number;

    /**
     * The total of bytes sent from the beginning of this RTP stream. From bytesSent outbound-rtp report
     */
    totalBytesSent?: number;

    /**
     * The total time for encoding the media from the beginning of this RTP stream. From totalEncodeTime in outbound-rtp report
     */
    totalEncodeTimeSeconds?: number;

    /**
     * The total number of video frames sent from the beginning of this RTP stream. From framesSent in outbound-rtp report
     */
    totalFramesSent?: number;

    /**
     * The number of frames that were successfully encoded from the beginning of this RTP stream.From framesEncoded in outbound-rtp report
     */
    totalFramesEncoded?: number;

    /**
     * The number of key frames that were successfully encoded from the beginning of this RTP stream.From keyFramesEncoded in outbound-rtp report
     */
    totalKeyFramesEncoded?: number;

    /**
     * The number of Picture Loss Indication (PLI) requests sent (receiver) or received (sender) from the beginning of this RTP stream.
     * From pliCount in outbound-rtp report
     */
    totalPliCount?: number;

    /**
     * The number of Negative Acknowledgment (NACK) requests sent from the beginning of this RTP stream. From nackCount in outbound-rtp report
     */
    totalNackCount?: number;

    /**
     * The current encoder target in bits per second for this RTP stream.From targetBitrate in outbound-rtp report
     */
    targetBitrate?: number;

    /**
     * The number of horizontal pixels in a video frame for this RTP stream.From frameWidth in inbound-rtp or outbound-rtp report
     */
    frameWidth?: number;

    /**
     * The number of vertical pixels in a video frame for this RTP stream.From frameHeigh in inbound-rtp or outbound-rtp report
     */
    frameHeight?: number;

    /**
     * The number of individual video frames sent per second at a given time.
     */
    framesPerSecond?: number;

    /**
     * The current reason for limiting the resolution and/or framerate. Acceptable values: bandwidth, cpu, other, none.
     */
    qualityLimitationReason?: string;

    /**
     * Object containing the details about all quality limitations durations during the session
     */
    qualityLimitationDurations?: any;

    /**
     * If exist, the value of the media identification-tag negotiated for this RTP streamFrom mid in inbound-rtp or outbound-rtp report
     */
    mid?: number;

    /**
     * The rate of retransmitted packets sent per second.
     * This metric is useful for evaluating the stability and quality of a WebRTC connection by indicating how many packets needed to be resent to maintain data integrity.
     */
    retransmittedPacketsSent?: number;

    /**
     * The average Quantization Parameter (QP) used for encoding a video stream, normalized by the number of frames encoded.
     * The Quantization Parameter (QP) is a crucial factor in video compression and directly affects the quality and bitrate of the video.
     * The Quantization Parameter (QP) controls the level of compression during video encoding. Lower QP values (e.g., around 10-20) correspond to higher video quality because the encoding process uses more bits to represent each frame.
     * Higher QP values (e.g., 30-50) result in higher compression and lower video quality, as fewer bits are used per frame.
     */
    qpValue?: number;

    /**
     * Name of the codec used (eg: "vp8")Computed from mimeType (remove "audio/") in the codec report associated to this RTP Stream (codecId from the inbound-rtp report)
     */
    codec?: string;
}

/**
 * Interface containing all stats related to the INBOUND video stats for the sessions
 * NOTE The same interface is also related to the screen sharing, as in terms of WEBRTC it's a VIDEO stream
 */
export interface WebrtcStatisticsInboundVideo {
    /**
     * The total of packets received from the beginning of this RTP stream. From packetsReceived in inbound-rtp report
     */
    totalPacketsReceived?: number;

    /**
     * The total of packets discarded from the beginning of this RTP stream.From packetsDiscarded in inbound-rtp report
     */
    totalPacketsReceivedDiscarded?: number;

    /**
     * The total of packets lost from the beginning of this RTP stream.From packetsLost in inbound-rtp report
     */
    totalPacketsLost?: number;

    /**
     * The total of bytes received from the beginning of this RTP stream.From bytesReceived inbound-rtp report
     */
    totalBytesReceived?: number;

    /**
     * The number of KB received for this RTP stream since the last measurement.
     */
    deltaKBReceived?: number;

    /**
     * The total time for decoding the media from the beginning of this RTP stream.From totalDecodeTime in inbound-rtp report
     */
    totalDecodeTimeSeconds?: number;

    /**
     * The total number of video frames received from the beginning of this RTP stream.From framesReceived in inbound-rtp report
     */
    totalFramesReceived?: number;

    /**
     * The number of frames that were successfully decoded from the beginning of this RTP stream.From framesDecoded in inbound-rtp report
     */
    totalFramesDecoded?: number;

    /**
     * The number of key frames that were successfully decoded from the beginning of this RTP stream.From keyFramesDecoded in inbound-rtp report
     */
    totalKeyFramesDecoded?: number;

    /**
     * The number of frames that were received but not displayed or processed (e.g., due to delays or quality degradation) from the beginning of this RTP stream.
     * From framesDropped in inbound-rtp report
     */
    totalFramesDropped?: number;

    /**
     * The number of video freeze experienced by the receiver from the beginning of this RTP stream.From freezeCount in inbound-rtp report
     */
    totalFreezeCount?: number;

    /**
     * The number of Picture Loss Indication (PLI) requests sent (receiver) or received (sender) from the beginning of this RTP stream.
     */
    totalPliCount?: number;

    /**
     * The number of Negative Acknowledgment (NACK) requests sent from the beginning of this RTP stream.From nackCount in outbound-rtp report
     */
    totalNackCount?: number;

    /**
     * The number of horizontal pixels in a video frame for this RTP stream.From frameWidth in inbound-rtp or outbound-rtp report
     */
    frameWidth?: number;

    /**
     * The number of vertical pixels in a video frame for this RTP stream. From frameHeigh in inbound-rtp or outbound-rtp report
     */
    frameHeight?: number;

    /**
     * The number of individual video frames received per second at a given time.
     */
    framesPerSecond?: number;

    /**
     * If exist, the value of the media identification-tag negotiated for this RTP streamFrom mid in inbound-rtp or outbound-rtp report
     */
    mid?: number;

    /**
     * The number of times the media playback has been paused. A pause can happen due to various reasons, including network conditions, user actions, or system-level issues such as performance bottlenecks.
     */
    pauseCount?: number;

    /**
     * Name of the codec used (eg: "vp8")Computed from mimeType (remove "audio/") in the codec report associated to this RTP Stream (codecId from the inbound-rtp report)
     */
    codec?: string;
}

/**
 * Interface containing all stats related to the video stats for the sessions inbound/outbound
 * NOTE The same interface is also related to the screen sharing, as in terms of WEBRTC it's a VIDEO stream
 */
export interface WebrtcStatisticsVideo {
    /**
     * Object containing all stats related to the outbound-rtp (outflow)
     * NB In case of SIMULCAST (only available for Bubble Conferences), there're 3 substreams send to the distant party ("l", "m", "h" for low, medium, high);
     */
    sent?: WebrtcStatisticsOutboundVideo | { l: WebrtcStatisticsOutboundVideo; m: WebrtcStatisticsOutboundVideo; h: WebrtcStatisticsOutboundVideo; };

    /**
     * Object containing all stats related to the inbound-rtp (inflow)
     */
    received?: WebrtcStatisticsInboundVideo;
}

/**
 * The type of the session
 */
export enum WebrtcStatisticsType {
    CONFERENCE = "Conference",
    WEBINAR = "Webinar",
    P2P = "P2P"
}

/**
 * The current media type of the session based on the kind of Media Streams inside
 */
export enum WebrtcStatisticsMediaType {
    /**
     * Audio only
     */
    AUDIO = "AUDIO",

    /**
     * BUNDLE (audio + video)
     */
    BUNDLE = "BUNDLE",

    /**
     * Video only
     */
    VIDEO = "VIDEO",

    /**
     * Screen Sharing only
     */
    SHARING = "SHARING"
}

/**
 * The type of the session in terms of media direction: publisher only / receiver only / both
 */
export enum WebrtcStatisticsSessionType {
    /**
     * Subscriber and publisher (bidirectionnal)
     */
    PUBSUB = "PUBSUB",

    /**
     * Subscriber (or receiver) of the media
     */
    SUBSCRIBER = "SUBSCRIBER",

    /**
     * Publisher of the media
     */
    PUBLISHER = "PUBLISHER"
}

export interface WebrtcStatisticsData {
    /**
     * The current iteration number of getting the statistics
     */
    iteration?: number;

    /**
     * The interval duration getting the statistics
     */
    intervalDuration?: number;

    /**
     * The type of the session related to the statistics;
     */
    type?: WebrtcStatisticsType;

    /**
     * In case the session is part of a bubble conference
     */
    conferenceId?: string;

    /**
     * In case the session is part of a webinar
     */
    webinarId?: string;

    /**
     * The session media type
     */
    mediaType?: WebrtcStatisticsMediaType;

    /**
     * The session type
     */
    sessionType?: WebrtcStatisticsSessionType;

    /**
     * The audio data, if available
     */
    audio?: WebrtcStatisticsAudio;

    /**
     * The video data, if available
     */
    video?: WebrtcStatisticsVideo;

    /**
     * The sharing data, if available
     */
    sharing?: WebrtcStatisticsVideo;

    /**
     * The network data, if available
     */
    network?: WebrtcStatisticsNetwork;

    /**
     * In case of web conference, the remote publisher id
     */
    publisherId?: string;

    /**
     * In case of web conference, the remote publisher name (anonymized)
     */
    publisherName?: string;
}

export class WebrtcSessionStatsHandler {

    private contactService: ContactService = ContactService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();

    constructor(private webrtcConfigAndUtils: WebrtcConfigAndUtils) { }

    private metricsValues = {
        /* MOS VALUES */
        MOSVeryHigh: 4.1,
        MOSHigh: 3.8,
        MOSMedium: 3.5,
        MOSMediocre: 1,

        /* AudioLevelValues */
        audioLevelBackground: 0.01,
        audioLevelActive: 0.1,
        audioLevelLoud: 0.9,

        /* DeJitter values */
        bufferBloatThreshold: 200,

        /* RTT values */
        RTTMediocre: 300,
        RTTBad: 200,
        RTTFair: 40,

        /* Packet Loss */
        packetLossBad: 1.5,
        packetLossMediocre: 4,

        /* Jitter values */
        jitterMediocre: 40,
        jitterBad: 25,
        jitterFair: 15,

        /* HighConcealment Values */
        concealmentThreshold: 10,

        /*  HighSpeakerDelay Values */
        speakerDelayThreshold: 50,

        /* not used for the moment */
        interruptionThreshold: 300,

        /* HighPacketsSendDelay Values */
        packetsSendDelayThreshold: 20
    };

    public setWebrtcMetricsValues(metrics) {
        //just in case, change only values that come from the object. If missing, keep default values
        if (metrics) {
            Object.keys(metrics).forEach((key) => {
                this.metricsValues[key] = metrics[key];
            });
        }

        this.logger.debug(`[WebrtcSessionStatsHandler] setWebrtcMetricsValues from server ${JSON.stringify(metrics)}`);
    }

    public getStatsForSession(session: JingleSession, interval: number = 5000, webinarId?: string) {
        let usedInterval: number = interval;
        if (localStorage.getItem("qaWebrtcStatsInterval")) {
            usedInterval = Number(localStorage.getItem("qaWebrtcStatsInterval"));
        }
        session.webrtcStats.intervalDuration = usedInterval;

        session.webrtcStatsInterval = setInterval(async () => {
            try {
                if (!session.peerconnection) {
                    //session does not exist anymore
                    session.terminate();
                    return;
                }
                const webrtcStatsData: WebrtcStatisticsData = {
                    type: WebrtcStatisticsType.P2P
                };

                if (session.conference_id && session.conference_id !== -1) {
                    if (session.isWebinarSession) { webrtcStatsData.type = WebrtcStatisticsType.WEBINAR; }
                    else { webrtcStatsData.type = WebrtcStatisticsType.CONFERENCE; }

                    if (webinarId) webrtcStatsData.webinarId = webinarId;
                    else webrtcStatsData.conferenceId = session.conference_id;

                    if (session.publisherId) {
                        webrtcStatsData.publisherId = session.publisherId;

                        //find the contact related to the publisher ID
                        const contact = this.contactService.getContactFromCacheById(session.publisherId);
                        if (contact) {
                            webrtcStatsData.publisherName = contact.toString();
                        }
                    }

                    if (webrtcStatsData.type === WebrtcStatisticsType.CONFERENCE) {
                        //calculate the MediaType and the SessionType for conf

                        if (session.localType === "audio") {
                            webrtcStatsData.mediaType = WebrtcStatisticsMediaType.AUDIO;
                            webrtcStatsData.sessionType = WebrtcStatisticsSessionType.PUBSUB;
                        }

                        if (session.localType === "video" || session.remoteType === "video") {
                            if (!session.isInitiator && !webrtcStatsData.publisherId) {
                                webrtcStatsData.publisherName = "DYNAMICFEED";
                            }

                            webrtcStatsData.mediaType = WebrtcStatisticsMediaType.VIDEO;
                            webrtcStatsData.sessionType = session.isInitiator ? WebrtcStatisticsSessionType.PUBLISHER : WebrtcStatisticsSessionType.SUBSCRIBER;
                        }
                    }
                    else if (webrtcStatsData.type === WebrtcStatisticsType.WEBINAR) {
                        //calculate the MediaType and the SessionType for webinar
                        if (session.localType === "audio+video") {

                            //we need to manage the current state of the incoming / outgoing streams in case of BUNDLE
                            webrtcStatsData.mediaType = WebrtcStatisticsMediaType.AUDIO;

                            //get the last transceiver and check the current direction
                            //for my session, there's one or two transceivers
                            //for distant session, there're 2 or 3 transceivers (there's an inactive one in the start ?)
                            if ((session.isInitiator && session.localTransceivers?.length > 1) || (!session.isInitiator && session.localTransceivers?.length > 2)) {
                                const transceiver = session.localTransceivers[session.localTransceivers?.length - 1];
                                if (transceiver?.currentDirection === 'sendonly' || transceiver?.currentDirection === 'recvonly') {
                                    webrtcStatsData.mediaType = WebrtcStatisticsMediaType.BUNDLE;
                                }
                            }

                            webrtcStatsData.sessionType = session.isInitiator ? WebrtcStatisticsSessionType.PUBLISHER : WebrtcStatisticsSessionType.SUBSCRIBER;
                        }
                    }

                    if (session.localType === "sharing" || session.remoteType === "sharing") {
                        webrtcStatsData.mediaType = WebrtcStatisticsMediaType.SHARING;
                        webrtcStatsData.sessionType = session.isInitiator ? WebrtcStatisticsSessionType.PUBLISHER : WebrtcStatisticsSessionType.SUBSCRIBER;
                    }

                    if (session.onHold) {
                        //prepare log
                        session.webrtcStats.video.videoPacketsReceivedDelta = -1;
                        const info = session.publisherId
                            ? `${webrtcStatsData.mediaType} ${webrtcStatsData.sessionType} ${session.publisherId} ${webrtcStatsData.publisherName}`
                            : `${webrtcStatsData.mediaType} ${webrtcStatsData.sessionType}`;
                        this.logger.info(`[WebrtcSessionStatsHandler] on getStatsForSession for session ${session.sid} -- ${info} -- on HOLD`);
                        return;
                    }
                }

                let usedInterval: number = interval;
                if (localStorage.getItem("qaWebrtcStatsInterval")) {
                    usedInterval = Number(localStorage.getItem("qaWebrtcStatsInterval"));
                }
                webrtcStatsData.intervalDuration = usedInterval;

                const stats = await session.peerconnection.getStats();
                // @ts-ignore
                const results = stats.entries();
                this.getStatsFromData(session, results, webrtcStatsData, webinarId);

                //check the remote audio stream
                if (webrtcStatsData.type === WebrtcStatisticsType.P2P) {
                    if (session.remoteStreamsObject[0] && session.remoteStreamsObject[0]?.getAudioTracks()[0]) {
                        if (!session.remoteStreamsObject[0].getAudioTracks()[0].enabled || session.remoteStreamsObject[0].getAudioTracks()[0].muted) {
                            this.logger.info(`[WebrtcSessionStatsHandler] on webrtcStats for session ${session.sid} -- remote audio track warn -- muted: ${session.remoteStreamsObject[0].getAudioTracks()[0].muted}, enabled: ${session.remoteStreamsObject[0].getAudioTracks()[0].enabled}`);
                        }
                    }
                }
            }
            catch (error) {
                this.logger.warn(`[WebrtcSessionStatsHandler] on getStatsForSession for session error -- ${stringifyError(error)}`);
            }
        }, usedInterval);

        return session.webrtcStatsInterval;
    }

    private getStatsFromData(session, stats, webrtcStatsData: WebrtcStatisticsData, webinarId?: string) {
        const sec = session.webrtcStats.intervalDuration / 1000;
        let id = "";
        //for conference, only keep stats for local sessions
        if ((webrtcStatsData.type === WebrtcStatisticsType.P2P) || (session.isInitiator && webrtcStatsData.type === WebrtcStatisticsType.CONFERENCE)) {
            if (!this.webrtcConfigAndUtils.callsStats[session.sid]) {
                this.webrtcConfigAndUtils.callsStats[session.sid] = {};
            }
            //update the stats for this call, but save the ID
            id = this.webrtcConfigAndUtils.callsStats[session.sid].id;
        }

        const metricsDataForServer: any[] = [];

        const codecMap = {};
        let result = stats.next();
        let localCandidateId;
        let remoteCandidateIp;
        let mosPacketsLostAudio = 0;
        let globalJitter = 0;
        let totalRoundTripTimeFromCandidate = 0;
        let responsesReceived = 0;
        let totalRoundTripTimeFromRTPReport = 0;
        let roundTripTimeMeasurements = 0;
        let deltaAvgRTT = session.webrtcStats.audio.lastDeltaAvgRTT || 100;
        let conferenceVideoFramesDecoded = 0;
        let deltaAudioOutputLevel = -1;

        let localAddr;
        let remoteAddr;
        const devMode = this.settingsService.getSetting("devMode") === "true";

        let outboundAudioStats: WebrtcStatisticsOutboundAudio;
        let inboundAudioStats: WebrtcStatisticsInboundAudio;

        let mediaPlayoutStats;

        //let ctrMedia = 0;
        while (!result.done) {
            // console.error(`${result.value[1]}`);
            const value = result.value[1];

            /* ICE MANAGEMENT */
            if (value.type === "candidate-pair" && value.nominated && value.writable) {
                //this is the ICE candidate pair that is selected
                localCandidateId = value.localCandidateId;
                remoteCandidateIp = value.remoteCandidateId;

                metricsDataForServer.push(value);
                totalRoundTripTimeFromCandidate = Math.floor(value.totalRoundTripTime * 1000);
                responsesReceived = value.responsesReceived;
            }

            if (localCandidateId && value.id === localCandidateId) {
                //this is the local candidate object
                session.webrtcStats.ice.networkType = value.networkType;
                const protocol = (value.candidateType === "relay" && value.relayProtocol) ? value.relayProtocol : value.protocol;

                session.webrtcStats.ice.local = value.address + ":" + value.port + " [" + value.candidateType + "] (" + protocol + ")";

                if (!devMode && value.candidateType !== "relay") {
                    localAddr = value.address.replace(/([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)[0-9]{1,3}/, "$1" + "x");
                    localAddr = localAddr.replace(/(([0-9a-z]{0,4}:){1,4})([0-9a-z]{1,4}:?){4}/, "$1" + "x:x:x:x");

                    localAddr = localAddr + ":" + value.port + " [" + value.candidateType + "] (" + protocol + ")";
                } else {
                    localAddr = session.webrtcStats.ice.local;
                }

                metricsDataForServer.push(value);
            }
            if (remoteCandidateIp && value.id === remoteCandidateIp) {
                //this is the remote candidate object
                session.webrtcStats.ice.remote = value.address + ":" + value.port + " [" + value.candidateType + "] (" + value.protocol + ")";

                if (!devMode && value.candidateType !== "relay") {
                    remoteAddr = value.address.replace(/([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)[0-9]{1,3}/, "$1" + "x");
                    remoteAddr = remoteAddr.replace(/(([0-9a-z]{0,4}:){1,4})([0-9a-z]{1,4}:?){4}/, "$1" + "x:x:x:x");

                    remoteAddr = remoteAddr + ":" + value.port + " [" + value.candidateType + "] (" + value.protocol + ")";

                } else {
                    remoteAddr = session.webrtcStats.ice.remote;
                }

                metricsDataForServer.push(value);
            }
            /* END OF ICE MANAGEMENT */

            /* *****************
            * MANAGE ALL SSRC
            * *****************/
            if (value.type === "inbound-rtp" || value.type === "outbound-rtp") {
                //check if ssrc already exists
                let isActiveRtp = true;
                if (value.type === "inbound-rtp" && !value.codecId) { isActiveRtp = false; }
                //chrome only
                if (window.adapter?.browserDetails?.browser === "chrome" && value.type === "outbound-rtp" && !value.mediaSourceId) { isActiveRtp = false; }

                if (!session.webrtcStats.ssrc[value.ssrc]) {
                    //if no codecId value, do not create inbound-rtp
                    if (isActiveRtp) {
                        const peerJid = (value.type === "inbound-rtp") ? this.xmppService.getBareJidFromJid(session.peerjid) : this.contactService.userContact.jid;
                        session.webrtcStats.ssrc[value.ssrc] = {
                            type: "stream",
                            ssrc: value.ssrc,
                            kind: value.kind,
                            direction: (value.type === "inbound-rtp") ? "inbound" : "outbound",
                            measures: 1,
                            lastMeasures: 0,
                            duration: sec,
                            startedAt: new Date().toISOString(),
                            originator: peerJid,
                            rules: {},
                            codecId: value.codecId,
                            deltaPacketsLost: 0,
                            deltaPackets: 0,
                            totalPacketsLost: 0
                        } as WebrtcStatisticsSsrc;

                        if (value.kind === "audio") {
                            session.webrtcStats.ssrc[value.ssrc].audioLevel = {
                                percentMuted: 0,
                                percentQuiet: 0,
                                percentBackground: 0,
                                percentActive: 0,
                                percentLoud: 0
                            };

                            session.webrtcStats.ssrc[value.ssrc].audioStats = {
                                oldTotalAudioEnergy: 0,
                                oldTotalSamplesDuration: 0,
                                muted: 0,
                                quiet: 0,
                                background: 0,
                                active: 0,
                                loud: 0,
                                oldConcealedSamples: 0,
                                oldTotalConcealedSamples: 0,
                                oldTotalPlayoutDelay: 0,
                                oldTotalSamplesCount: 0,
                                oldPacketsDiscarded: 0,
                                oldTotalPacketSendDelay: 0
                            };
                        }
                    }
                }
                else {
                    if (!session.webrtcStats.ssrc[value.ssrc].codecId && !session.webrtcStats.ssrc[value.ssrc].codec) { session.webrtcStats.ssrc[value.ssrc].codecId = value.codecId; }

                    //check if outbound or inbound are not 'stopped' so that do not update the measures  and the duration
                    if (isActiveRtp) {
                        session.webrtcStats.ssrc[value.ssrc].measures += 1;
                        session.webrtcStats.ssrc[value.ssrc].duration = sec * session.webrtcStats.ssrc[value.ssrc].measures;
                    }

                    if (session.webrtcStats.ssrc[value.ssrc].codecId && codecMap[session.webrtcStats.ssrc[value.ssrc].codecId]) {
                        let mimeType = codecMap[session.webrtcStats.ssrc[value.ssrc].codecId];
                        mimeType = mimeType.replace("audio/", "");
                        mimeType = mimeType.replace("video/", "");
                        delete session.webrtcStats.ssrc[value.ssrc].codecId;
                        session.webrtcStats.ssrc[value.ssrc].codec = mimeType;
                    }
                }

                if (isActiveRtp) {
                    if (value.type === "inbound-rtp" && value.kind === "audio" && session.webrtcStats.ssrc[value.ssrc]) {
                        //manage inbound audio stats
                        //calculate first no audio before audio level !
                        this.calculateNoAudio(value, session, value.ssrc);
                        this.calculateAudioLevelValuesForSSRC(value, session, value.ssrc);
                        this.calculateHighConcealmentValuesForSSRC(value, session, value.ssrc);
                        this.calculateLateAudioValuesForSSRC(value, session, value.ssrc);

                    }
                    else if (value.type === "outbound-rtp" && value.kind === "audio" && session.webrtcStats.ssrc[value.ssrc]) {
                        //save the mediaSourceId and get the audio values from there
                        session.webrtcStats.ssrc[value.ssrc].mediaSourceId = value.mediaSourceId;
                        this.calculateHighSendAudioDelay(value, session, value.ssrc);
                    }

                    if (value.type === "inbound-rtp" || value.type === "outbound-rtp") {
                        //check for delta packets
                        this.calculateNoPackets(value, session, value.ssrc);
                    }
                }

            }

            /* *****************
            * AUDIO STREAMS STATS
            * *****************/

            if (value.type === "inbound-rtp" && (value.mediaType === "audio" || value.kind === "audio") && value?.mid !== "3") {
                //find codec ID
                session.webrtcStats.codecId = value.codecId;

                //incoming audio stream
                if (!inboundAudioStats) inboundAudioStats = {};
                inboundAudioStats.totalPacketsReceived = value.packetsReceived;
                inboundAudioStats.totalPacketsLost = value.packetsLost;
                inboundAudioStats.totalPacketsReceivedDiscarded = value.packetsDiscarded;
                inboundAudioStats.totalBytesReceived = value.bytesReceived;
                inboundAudioStats.audioOutputLevel = value.audioLevel;
                inboundAudioStats.mid = 0;
                inboundAudioStats.jitterBufferMs = 0;
                inboundAudioStats.ssrc = value.ssrc;
                inboundAudioStats.totalFecPacketsUsed = 0;

                if (value.fecPacketsReceived && value.fecPacketsDiscarded) { inboundAudioStats.totalFecPacketsUsed = value.fecPacketsReceived - value.fecPacketsDiscarded; }

                inboundAudioStats.totalAudioEnergy = value.totalAudioEnergy;
                if (value.jitterBufferEmittedCount) {
                    inboundAudioStats.processingMs = value.totalProcessingDelay / value.jitterBufferEmittedCount - value.jitterBufferDelay / value.jitterBufferEmittedCount;
                }

                inboundAudioStats.totalSamplesCount = 0;
                if (mediaPlayoutStats?.totalSamplesCount) {
                    inboundAudioStats.playoutDelayMs = Math.floor((mediaPlayoutStats.totalPlayoutDelay / mediaPlayoutStats.totalSamplesCount) * 1000);
                    inboundAudioStats.totalSamplesCount = mediaPlayoutStats?.totalSamplesCount;
                }

                const totalAudioPacketsReceived = value.packetsReceived;
                const totalAudioPacketsLost = value.packetsLost;

                globalJitter = parseInt(value.jitterBufferDelay, 10);
                const jitterBufferEmittedCount = parseInt(value.jitterBufferEmittedCount, 10);

                if (value.jitter) { inboundAudioStats.jitterMs = value.jitter * 1000; }

                session.webrtcStats.audio.jitter = value.jitter;

                inboundAudioStats.totalJitterBufferEmittedCount = jitterBufferEmittedCount;
                inboundAudioStats.totalConcealedSamples = value.concealedSamples;
                inboundAudioStats.totalSamplesReceived = value.totalSamplesReceived;

                if (jitterBufferEmittedCount && globalJitter) {
                    inboundAudioStats.jitterBufferMs = Math.floor((globalJitter / jitterBufferEmittedCount) * 1000);
                }

                if (session.webrtcStats.audio.lastAudioOutputLevel === -1) { session.webrtcStats.audio.lastAudioOutputLevel = inboundAudioStats.audioOutputLevel; }
                else {
                    //calculate the delta
                    deltaAudioOutputLevel = session.webrtcStats.audio.lastAudioOutputLevel - inboundAudioStats.audioOutputLevel;
                    session.webrtcStats.audio.lastAudioOutputLevel = inboundAudioStats.audioOutputLevel;
                }

                let mosPacketsReceivedAudio = totalAudioPacketsReceived - session.webrtcStats.audio.totalAudioPacketsReceived;
                session.webrtcStats.audio.totalAudioPacketsReceived = totalAudioPacketsReceived;
                if (mosPacketsReceivedAudio < 0) {
                    mosPacketsReceivedAudio = 0;
                }

                inboundAudioStats.deltaPacketsReceived = mosPacketsReceivedAudio;

                mosPacketsLostAudio = totalAudioPacketsLost - session.webrtcStats.audio.totalAudioPacketsLost;
                session.webrtcStats.audio.totalAudioPacketsLost = totalAudioPacketsLost;

                if (mosPacketsLostAudio < 0) {
                    mosPacketsLostAudio = 0;
                }
                const bytes = value.bytesReceived - session.webrtcStats.audio.bytesReceived;
                session.webrtcStats.audio.bytesReceived = value.bytesReceived;

                inboundAudioStats.deltaKBReceived = Math.floor((bytes * 8) / (1000 * sec));

                if (inboundAudioStats.deltaKBReceived < 0) inboundAudioStats.deltaKBReceived = 0;

                //get concealment values

                if (session.webrtcStats.ssrc[value.ssrc]) {
                    session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost = mosPacketsLostAudio;
                    session.webrtcStats.ssrc[value.ssrc].totalAudioPacketsLost = value.packetsLost;

                    session.webrtcStats.ssrc[value.ssrc].jitterMs = inboundAudioStats.jitterMs ? Math.round(inboundAudioStats.jitterMs) : 0;

                    //only if active and  media-playout data is found
                    if (mediaPlayoutStats && value.codecId) {
                        this.calculateHighSpeakerDelayForSSRC(mediaPlayoutStats, session, inboundAudioStats.ssrc);
                    }
                }

                metricsDataForServer.push(value);
            }

            /* *****************
            * HIGH SPEAKER DELAY
            * *****************/
            //media-playout stats are get before the inbound stats, so we save them for later
            if (value.type === "media-playout" && value.kind === "audio") {
                //find the current input audio ssrc
                if (inboundAudioStats?.ssrc && session.webrtcStats.ssrc[inboundAudioStats.ssrc]) {
                    this.calculateHighSpeakerDelayForSSRC(value, session, inboundAudioStats.ssrc);
                }
                //if it does not exist, save for later
                if (!mediaPlayoutStats) { mediaPlayoutStats = value; }

                const deltaSamplesDuration = value.totalSamplesDuration - session.webrtcStats.audio.oldTotalSamplesDurationMediaPlayout;
                const deltaSynthesizedDuration = value.synthesizedSamplesDuration - session.webrtcStats.audio.oldTotalSynthesizedSamplesDuration;

                if (deltaSamplesDuration) {
                    session.webrtcStats.audio.synthesizedSamplesPlayed = Math.round((deltaSynthesizedDuration / deltaSamplesDuration) * 100);
                }

                //save the synthezisedSamples
                session.webrtcStats.audio.oldTotalSamplesDurationMediaPlayout = value.totalSamplesDuration;
                session.webrtcStats.audio.oldTotalSynthesizedSamplesDuration = value.synthesizedSamplesDuration;
            }

            /* STREAMS STATS -- OUTBOUND AUDIO RTP */
            //ignore audio on top of sharing stats
            if (value.type === "outbound-rtp" && (value.mediaType === "audio" || value.kind === "audio") && value?.mid !== "3") {

                if (!outboundAudioStats) outboundAudioStats = {};
                outboundAudioStats.mid = 0;
                outboundAudioStats.totalPacketsSent = value.packetsSent;
                outboundAudioStats.totalBytesSent = value.bytesSent;
                outboundAudioStats.targetBitrate = value.targetBitrate;

                outboundAudioStats.totalAudioEnergy = value.totalAudioEnergy;

                session.webrtcStats.audio.totalAudioPacketsSent = value.packetsSent;

                const bytes = value.bytesSent - session.webrtcStats.audio.bytesSent;
                session.webrtcStats.audio.bytesSent = value.bytesSent;

                outboundAudioStats.deltaAudioKBSent = Math.floor((bytes * 8) / (1000 * sec));
                if (value.codecId) {
                    session.webrtcStats.codecId = value.codecId;
                }

                metricsDataForServer.push(value);
            }

            /* STREAMS STATS -- REMOTE INBOUND RTP */
            if (value.type === "remote-outbound-rtp" && value.kind === "audio") {
                //we should ge the Round Trip Time
                if (value.totalRoundTripTime) {
                    totalRoundTripTimeFromRTPReport = Math.floor(value.totalRoundTripTime * 1000);
                    roundTripTimeMeasurements = value.roundTripTimeMeasurements;
                }
            }

            if (value.type === "remote-inbound-rtp" && value.kind === "audio") {
                //this is the feedback from the other party abouh the incoming RTP flow
                //we should ge the Round Trip Time
                if (value.totalRoundTripTime && !totalRoundTripTimeFromRTPReport) {
                    totalRoundTripTimeFromRTPReport = Math.floor(value.totalRoundTripTime * 1000);
                    roundTripTimeMeasurements = value.roundTripTimeMeasurements;
                }
            }

            /* REMOTE-INBOUND-RTP stats */
            if (value.type === "remote-inbound-rtp") {
                //this is the feedback from the other party abouh the incoming RTP flow
                //get the packet lost values feedback from the distant
                if (value.ssrc && session.webrtcStats.ssrc[value.ssrc]) {
                    //calculate the new delta
                    session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost = value.packetsLost - session.webrtcStats.ssrc[value.ssrc].totalPacketsLost;
                    if (session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost < 0) { session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost = 0; }
                    session.webrtcStats.ssrc[value.ssrc].totalPacketsLost = value.packetsLost;

                    if (value.kind === "audio") {
                        //calculate the jitter MS
                        session.webrtcStats.ssrc[value.ssrc].jitterMs = value.jitter ? Math.round(value.jitter * 1000) : 0;
                    }
                }
            }

            /* STREAMS STATS --MEDIA SOURCE */
            if (value.type === "media-source" && value.kind === "audio") {
                //this is the feedback from the media-source
                if (!outboundAudioStats) outboundAudioStats = {};
                outboundAudioStats.audioInputLevel = parseFloat(value.audioLevel);

                if (session.webrtcStats.ssrc) {
                    Object.values(session.webrtcStats.ssrc).forEach((ssrc: WebrtcStatisticsSsrc) => {
                        if (ssrc.mediaSourceId === value.id) {
                            //calculate first no audio before audio level !
                            this.calculateNoAudio(value, session, ssrc.ssrc, outboundAudioStats);
                            this.calculateAudioLevelValuesForSSRC(value, session, ssrc.ssrc);
                        }
                    });
                }

            }

            /* AUDIO CODEC USED */
            if (session.webrtcStats.codecId && value.id === session.webrtcStats.codecId) {
                //get the codec type
                if (value.mimeType) {
                    session.webrtcStats.codec = value.mimeType.replace("audio/", "");
                }
                metricsDataForServer.push(value);
            }

            /* *****************
            * VIDEO STREAMS STATS
            * *****************/

            if (value.type === "inbound-rtp" && (value.mediaType === "video" || value.kind === "video")) {
                let mid = value.mid;
                const tempData: WebrtcStatisticsInboundVideo = {
                    totalPacketsReceived: value.packetsReceived,
                    totalPacketsReceivedDiscarded: value.packetsDiscarded,
                    totalPacketsLost: value.packetsLost,
                    totalBytesReceived: value.bytesReceived,
                    totalDecodeTimeSeconds: value.totalDecodeTime,
                    totalFramesReceived: value.framesReceived,
                    totalFramesDecoded: value.framesDecoded,
                    totalKeyFramesDecoded: value.keyFramesDecoded,
                    totalFramesDropped: value.framesDropped,
                    totalFreezeCount: value.freezeCount,
                    totalPliCount: value.pliCount,
                    totalNackCount: value.nackCount,
                    frameHeight: value.frameHeight,
                    frameWidth: value.frameWidth,
                    framesPerSecond: value.framesPerSecond,
                    mid: value.mid,
                    pauseCount: value.pauseCount
                };

                if (value.ssrc && session.webrtcStats.ssrc[value.ssrc]) {
                    //calculate the new delta
                    session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost = value.packetsLost - session.webrtcStats.ssrc[value.ssrc].totalPacketsLost;
                    if (session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost < 0) { session.webrtcStats.ssrc[value.ssrc].deltaPacketsLost = 0; }
                    session.webrtcStats.ssrc[value.ssrc].totalPacketsLost = value.packetsLost;
                }

                if (webrtcStatsData.type === WebrtcStatisticsType.P2P) {
                    //if no media source ID, then the outbound rtp is not active
                    //mid does not exist in Firefox, find another way to detect sharing
                    if (!mid) {
                        //try to detect that this is sharing
                        if (tempData.frameHeight > 900) mid = "2";
                    }

                    if (mid === "2") {
                        //sharing
                        if (!webrtcStatsData.sharing) { webrtcStatsData.sharing = {}; }
                        webrtcStatsData.sharing.received = tempData;

                        const bytes = tempData.totalBytesReceived - session.webrtcStats.sharing.bytesReceived;
                        session.webrtcStats.sharing.bytesReceived = tempData.totalBytesReceived;
                        webrtcStatsData.sharing.received.deltaKBReceived = Math.floor((bytes * 8) / (1000 * sec));
                    }
                    else {
                        //video
                        if (!webrtcStatsData.video) { webrtcStatsData.video = {}; }
                        webrtcStatsData.video.received = tempData;

                        const bytes = tempData.totalBytesReceived - session.webrtcStats.video.bytesReceived;
                        session.webrtcStats.video.bytesReceived = tempData.totalBytesReceived;
                        webrtcStatsData.video.received.deltaKBReceived = Math.floor((bytes * 8) / (1000 * sec));
                    }
                }
                else if (webrtcStatsData.mediaType === WebrtcStatisticsMediaType.BUNDLE) {
                    //if it's my session for webinar, there's no incoming video (only outgoing)
                    if (!session.isInitiator) {
                        if (!webrtcStatsData.video) { webrtcStatsData.video = {}; }
                        webrtcStatsData.video.received = tempData;

                        const bytes = tempData.totalBytesReceived - session.webrtcStats.video.bytesReceived;
                        session.webrtcStats.video.bytesReceived = tempData.totalBytesReceived;
                        webrtcStatsData.video.received.deltaKBReceived = Math.floor((bytes * 8) / (1000 * sec));
                    }
                }
                else {
                    if (webrtcStatsData.sessionType === WebrtcStatisticsSessionType.PUBLISHER) {
                        //if i'm the publisher, ignore the inbound data, it will be empty
                    }
                    else if (webrtcStatsData.type === WebrtcStatisticsType.WEBINAR && webrtcStatsData.mediaType === WebrtcStatisticsMediaType.AUDIO) {
                        //do not add the incoming video data, as the video is stopped
                    }
                    else {
                        //calculate the delta received packets
                        if (!session.webrtcStats.video.totalVideoPacketsReceived) {
                            //this is the first time
                            session.webrtcStats.video.totalVideoPacketsReceived = tempData.totalPacketsReceived;
                        }
                        else {
                            const framesReceivedDelta = tempData.totalPacketsReceived - session.webrtcStats.video.totalVideoPacketsReceived;
                            if (framesReceivedDelta === 0) {
                                session.webrtcStats.video.videoPacketsReceivedDeltaEmptyCount += 1;
                            }
                            else { session.webrtcStats.video.videoPacketsReceivedDeltaEmptyCount = 0; }

                            session.webrtcStats.video.videoPacketsReceivedDelta = framesReceivedDelta;
                            session.webrtcStats.video.totalVideoPacketsReceived = tempData.totalPacketsReceived;
                        }

                        if (webrtcStatsData.mediaType === WebrtcStatisticsMediaType.SHARING) {
                            if (!webrtcStatsData.sharing) { webrtcStatsData.sharing = {}; }
                            webrtcStatsData.sharing.received = tempData;

                            const bytes = tempData.totalBytesReceived - session.webrtcStats.sharing.bytesReceived;
                            session.webrtcStats.sharing.bytesReceived = tempData.totalBytesReceived;
                            webrtcStatsData.sharing.received.deltaKBReceived = Math.floor((bytes * 8) / (1000 * sec));
                        }
                        else {
                            conferenceVideoFramesDecoded = (session?.webrtcStats?.video) ? session.webrtcStats.video.framesDecoded : 0;
                            if (!webrtcStatsData.video) { webrtcStatsData.video = {}; }
                            webrtcStatsData.video.received = tempData;
                            session.webrtcStats.video.framesDecoded = tempData.totalFramesDecoded;

                            const bytes = tempData.totalBytesReceived - session.webrtcStats.video.bytesReceived;
                            session.webrtcStats.video.bytesReceived = tempData.totalBytesReceived;
                            webrtcStatsData.video.received.deltaKBReceived = Math.floor((bytes * 8) / (1000 * sec));
                        }
                    }
                }

                if (mid === "2" || webrtcStatsData.mediaType === WebrtcStatisticsMediaType.SHARING) {
                    session.webrtcStats.sharingCodecId = value.codecId;
                    if (session.webrtcStats.sharingCodec) { tempData.codec = session.webrtcStats.sharingCodec; }
                }
                else {
                    session.webrtcStats.videoCodecId = value.codecId;
                    if (session.webrtcStats.videoCodec) { tempData.codec = session.webrtcStats.videoCodec; }
                }

                metricsDataForServer.push(value);
            }

            if (value.type === "outbound-rtp" && (value.mediaType === "video" || value.kind === "video")) {
                let mid = value.mid;
                const contentType = value.contentType;
                const mediaSourceId = value.mediaSourceId;
                const tempData: WebrtcStatisticsOutboundVideo = {
                    totalPacketsSent: value.packetsSent,
                    totalBytesSent: value.bytesSent,
                    totalEncodeTimeSeconds: value.totalEncodeTime,
                    totalFramesSent: value.framesSent,
                    totalFramesEncoded: value.framesEncoded,
                    totalKeyFramesEncoded: value.keyFramesEncoded,
                    totalPliCount: value.pliCount,
                    totalNackCount: value.nackCount,
                    targetBitrate: value.targetBitrate,
                    frameHeight: value.frameHeight,
                    frameWidth: value.frameWidth,
                    framesPerSecond: value.framesPerSecond,
                    qualityLimitationReason: value.qualityLimitationReason,
                    qualityLimitationDurations: value.qualityLimitationDurations,
                    retransmittedPacketsSent: value.retransmittedPacketsSent,
                    mid: value.mid
                };

                if (value.framesEncoded && value.qpSum) {
                    tempData.qpValue = Math.floor(value.qpSum / value.framesEncoded);
                }

                if (webrtcStatsData.type === WebrtcStatisticsType.P2P) {
                    //if no media source ID, then the outbound rtp is not active (for chrome)
                    if ((window.adapter && window.adapter.browserDetails.browser !== "chrome") || mediaSourceId) {
                        //mid does not exist in Firefox, find another way to detect sharing
                        if (!mid) {
                            //try to detect that this is sharing
                            if (tempData.frameHeight > 900) mid = "2";
                        }

                        if (contentType === "sharing" || mid === "2") {
                            //sharing
                            if (!webrtcStatsData.sharing) { webrtcStatsData.sharing = {}; }
                            webrtcStatsData.sharing.sent = tempData;
                        }
                        else {
                            //video
                            if (!webrtcStatsData.video) { webrtcStatsData.video = {}; }
                            webrtcStatsData.video.sent = tempData;
                        }
                    }
                }
                else {
                    if (webrtcStatsData.sessionType === WebrtcStatisticsSessionType.PUBLISHER && webrtcStatsData.mediaType === WebrtcStatisticsMediaType.SHARING) {
                        //normal sharing send
                        if (!webrtcStatsData.sharing) { webrtcStatsData.sharing = {}; }
                        if (!webrtcStatsData.sharing.sent) { webrtcStatsData.sharing.sent = {}; }
                        webrtcStatsData.sharing.sent = tempData;
                    }
                    else if (webrtcStatsData.sessionType === WebrtcStatisticsSessionType.PUBLISHER && (webrtcStatsData.mediaType === WebrtcStatisticsMediaType.VIDEO || webrtcStatsData.mediaType === WebrtcStatisticsMediaType.BUNDLE)) {
                        //if i'm the publisher, we need to manage the simulcast
                        //we need to manage via the "rid" values
                        if (!webrtcStatsData.video) { webrtcStatsData.video = {}; }
                        if (!webrtcStatsData.video.sent) { webrtcStatsData.video.sent = {}; }

                        //in case of conference, try to build simulcast structure for firefox
                        if (webrtcStatsData.sessionType === WebrtcStatisticsSessionType.PUBLISHER && webrtcStatsData.mediaType === WebrtcStatisticsMediaType.VIDEO) {
                            let rid = value.rid;
                            if (!rid) {
                                //firefox does not have rid data
                                if (tempData.frameHeight < 360) rid = "l";
                                else if (tempData.frameHeight > 540) rid = "h";
                                else rid = "m";
                            }

                            webrtcStatsData.video.sent[rid] = tempData;
                        }
                        else { webrtcStatsData.video.sent = tempData; }
                    }
                }

                if ((contentType === "sharing" || mid === "2") || (webrtcStatsData.sessionType === WebrtcStatisticsSessionType.PUBLISHER && webrtcStatsData.mediaType === WebrtcStatisticsMediaType.SHARING)) {
                    session.webrtcStats.sharingCodecId = value.codecId;
                    if (session.webrtcStats.sharingCodec) { tempData.codec = session.webrtcStats.sharingCodec; }
                }
                else {
                    session.webrtcStats.videoCodecId = value.codecId;
                    if (session.webrtcStats.videoCodec) { tempData.codec = session.webrtcStats.videoCodec; }
                }

                metricsDataForServer.push(value);
            }

            /* VIDEO CODEC USED */
            if (session.webrtcStats.videoCodecId && value.id === session.webrtcStats.videoCodecId) {
                //get the codec type
                if (value.mimeType) {
                    session.webrtcStats.videoCodec = value.mimeType.replace("video/", "");
                }
            }

            if (session.webrtcStats.sharingCodecId && value.id === session.webrtcStats.sharingCodecId) {
                //get the codec type
                if (value.mimeType) {
                    session.webrtcStats.sharingCodec = value.mimeType.replace("video/", "");
                }
            }

            //save all codecs in the map
            if (value.type === "codec") {
                codecMap[value.id] = value.mimeType;
            }

            result = stats.next();
            //ctrMedia++;
        }

        let totalRTT = session.webrtcStats.audio.globalRTT;
        let rttMeasurements = session.webrtcStats.audio.lastRoundTripTimeMeasurements;
        //for JANUS, the round trip time remains blocked at 0; Then use the value from the STUN candidate pair

        if (totalRoundTripTimeFromRTPReport && roundTripTimeMeasurements) {
            totalRTT = totalRoundTripTimeFromRTPReport;
            rttMeasurements = roundTripTimeMeasurements;
        }
        else if (totalRoundTripTimeFromCandidate && responsesReceived) {
            totalRTT = totalRoundTripTimeFromCandidate;
            rttMeasurements = responsesReceived;
        }

        //calculate RTT
        if (rttMeasurements - session.webrtcStats.audio.lastRoundTripTimeMeasurements > 0) {
            deltaAvgRTT = Math.floor((totalRTT - session.webrtcStats.audio.globalRTT) / (rttMeasurements - session.webrtcStats.audio.lastRoundTripTimeMeasurements));
            if (deltaAvgRTT < 0) deltaAvgRTT = 0;
            session.webrtcStats.audio.lastRoundTripTimeMeasurements = roundTripTimeMeasurements;
            session.webrtcStats.audio.globalRTT = totalRTT;
            session.webrtcStats.audio.lastDeltaAvgRTT = deltaAvgRTT;
        }

        if (inboundAudioStats) inboundAudioStats.roundTripTimeMs = deltaAvgRTT;

        //calculate MOS
        let MOS = 0;
        try {
            if (inboundAudioStats?.deltaPacketsReceived) {
                let currentFlag = 0;
                const absoluteDelay: number = 20 + inboundAudioStats.jitterBufferMs + (deltaAvgRTT / 2);
                const EIpl = (mosPacketsLostAudio / (50 * sec)) * 100; //Calculate Ipl each second(percent packet lost)

                // If the codec used is G7xx, use the G107 Simplified MOS algorithm (This is the one used currently):
                if (session.webrtcStats.codec !== "opus") {
                    const Rx = 93.2 - EIpl;
                    const Ry = 0.18 * Rx * Rx - 27.9 * Rx + 1126.62;
                    const H = (absoluteDelay - 177.3 < 0) ? 0 : 1;
                    const Id = 0.024 * absoluteDelay + 0.11 * (absoluteDelay - 177.3) * H;
                    const R = Ry - Id;

                    if (R < 0 || EIpl > 20) { MOS = 1; }
                    else if (R > 100) { MOS = 4.5; }
                    else { MOS = 1 + 0.035 * R + R * (R - 60) * (100 - R) * 7 * 0.000001; }

                    if (R >= 80) {
                        currentFlag = 2;
                    }
                    else if (R < 80 && R >= 60) {
                        currentFlag = 1;
                    }
                }
                //If the codec is Opus, the G107.2 MOS algorithm should be used
                else {
                    const Ro = 148; // basic signal to noise ratio
                    const Is = 0; // Simultaneous impairment factor
                    let Id = 0; // Delay impairment factor
                    const A = 0; // Advantage factor

                    const Iee = 10.2 + (132 - 10.2) * (EIpl / (EIpl + 4.3));

                    if (absoluteDelay <= 100) {
                        Id = 0;
                    } else {
                    // calculate Delay impairment factor
                        const x = (Math.log(absoluteDelay) - Math.log(100)) / Math.log(2);
                        Id = 1.48 * 25 * (Math.pow(1 + Math.pow(x, 6), 1 / 6) - 3 * Math.pow(1 + Math.pow(x / 3, 6), 1 / 6) + 2);
                    }

                    // For FB, the basic E-model formula (7-1 of [ITU-T G.107]) can be re-written as:
                    const Rx = Ro - Is - Id - Iee + A;

                    // transmission rating factor
                    const R = Rx / 1.48;

                    if (R < 0 || EIpl > 20) { MOS = 1; }
                    else if (R > 100) { MOS = 4.5; }
                    else { MOS = 1 + 0.035 * R + R * (R - 60) * (100 - R) * 7 * 0.000001; }

                    if (MOS < 1) MOS = 1;

                    if (R >= 80) {
                        currentFlag = 2;
                    }
                    else if (R < 80 && R >= 60) {
                        currentFlag = 1;
                    }
                }

                inboundAudioStats.mos = MOS;
                inboundAudioStats.absDelayMs = absoluteDelay;
                inboundAudioStats.networkQualityValue = currentFlag;
            }
        }
        catch (error) {
            this.logger.warn(`[WebrtcSessionStatsHandler] on webrtcStats for session ${session.sid} error on MOS calculation ${stringifyError(error)}`);
        }

        let browserName = "web";
        if (window.adapter?.browserDetails.browser) {
            const browserData = window.navigator.userAgent.toLowerCase();

            if (browserData.includes("chrome")) {
                browserName = "chrome";

                if (browserData.includes("opr")) {
                    browserName = "opera";
                } else if (browserData.includes("edg")) {
                    browserName = "edge";
                }
            } else {
                browserName = window.adapter.browserDetails.browser;
            }
        }

        if (MOS && session.webrtcStats.audio.totalAudioPacketsReceived) {
            session.webrtcStats.iteration += 1;
            session.webrtcStats.audio.globalMOS += MOS;
            session.webrtcStats.audio.meanMOS = session.webrtcStats.audio.globalMOS / session.webrtcStats.iteration;
            session.webrtcStats.audio.maxRTT = (session.webrtcStats.audio.maxRTT < deltaAvgRTT) ? deltaAvgRTT : session.webrtcStats.audio.maxRTT;
            if (inboundAudioStats.jitterMs) session.webrtcStats.audio.globalJitter += inboundAudioStats.jitterMs;
            session.webrtcStats.audio.maxJitter = (session.webrtcStats.audio.maxJitter < inboundAudioStats.jitterMs) ? inboundAudioStats.jitterMs : session.webrtcStats.audio.maxJitter;

            const rainMetrics = {
                type: "mscore",
                meanRTT: Math.round((session.webrtcStats.audio.globalRTT / session.webrtcStats.audio.lastRoundTripTimeMeasurements) * 100) / 100,
                maxRTT: session.webrtcStats.audio.maxRTT,
                meanJitter: Math.round((session.webrtcStats.audio.globalJitter / session.webrtcStats.iteration) * 100) / 100,
                maxJitter: session.webrtcStats.audio.maxJitter,
                candidatePair: session.webrtcStats.ice.local + " <-> " + session.webrtcStats.ice.remote,
                networkType: session.webrtcStats.ice.networkType,
                meanMOS: session.webrtcStats.audio.meanMOS,
                packetsLost: session.webrtcStats.audio.totalAudioPacketsLost,
                packetsReceived: session.webrtcStats.audio.totalAudioPacketsReceived,
                codec: session.webrtcStats.codec,
                browser: browserName
            };

            metricsDataForServer.push(rainMetrics);

            inboundAudioStats.meanMOS = session.webrtcStats.audio.meanMOS;
            webrtcStatsData.iteration = session.webrtcStats.iteration;
        }
        else {
            metricsDataForServer.push({ browser: browserName });
        }

        //push all ssrc data
        if (session.webrtcStats.ssrc) {
            Object.values(session.webrtcStats.ssrc).forEach((ssrc: WebrtcStatisticsSsrc) => {

                //check if measures have changed
                if (ssrc.measures !== ssrc.lastMeasures) {
                    ssrc.lastMeasures = ssrc.measures;
                    if (ssrc.ssrc === inboundAudioStats?.ssrc) {
                        //calculate the mos values and %
                        this.calculateMosValuesForSsrc(ssrc, inboundAudioStats, session);
                        this.calculateRangeHighDejitterForSsrc(ssrc, inboundAudioStats, session);
                        this.calculateRangeRttForSsrc(ssrc, inboundAudioStats, session);
                    }
                    else if (ssrc.kind === "audio" && ssrc.direction === "outbound") {
                        this.calculateRangeRttForSsrc(ssrc, inboundAudioStats, session);
                    }

                    this.calculateHighJitterForSsrc(ssrc, session);
                    this.calculateHighPacketLossForSsrc(ssrc, session);
                }

                const ssrcData: WebrtcStatisticsSsrc = {
                    type: ssrc.type,
                    direction: ssrc.direction,
                    kind: ssrc.kind,
                    ssrc: ssrc.ssrc,
                    duration: ssrc.duration,
                    measures: ssrc.measures,
                    codec: ssrc.codec,
                    originator: ssrc.originator,
                    startedAt: ssrc.startedAt,
                    rules: ssrc.rules,
                };
                if (ssrc.mos) { ssrcData.mos = ssrc.mos; }
                if (ssrc.audioLevel) { ssrcData.audioLevel = ssrc.audioLevel; }

                metricsDataForServer.push(ssrcData);
            });
        }

        if (!webrtcStatsData.network) { webrtcStatsData.network = {}; }
        webrtcStatsData.network.networkType = session.webrtcStats.ice.networkType;
        webrtcStatsData.network.candidatePair = localAddr + " <-> " + remoteAddr;

        if (inboundAudioStats) {
            if (!webrtcStatsData.audio) webrtcStatsData.audio = {};
            inboundAudioStats.rms = session.webrtcStats.audio.rms;
            inboundAudioStats.synthesizedSamplesPlayed = session.webrtcStats.audio.synthesizedSamplesPlayed;
            webrtcStatsData.audio.received = inboundAudioStats;
            webrtcStatsData.audio.received.codec = session.webrtcStats.codec;
        }
        if (outboundAudioStats) {
            if (!webrtcStatsData.audio) webrtcStatsData.audio = {};
            webrtcStatsData.audio.sent = outboundAudioStats;
            webrtcStatsData.audio.sent.codec = session.webrtcStats.codec;
        }

        if (webrtcStatsData.conferenceId || webrtcStatsData.webinarId) {
            session.connection.jingle.rxSubject.next({ name: 'transceiverStats', data: { sid: session.sid } });
            if (session.webrtcStats.video.videoPacketsReceivedDeltaEmptyCount >= 5) {
                //should drop and resubscribe to session
                session.connection.jingle.rxSubject.next({ name: 'resubscriptionNeeded', data: { sid: session.sid, confId: session.conference_id } });
            }

            if (!session.isInitiator && session.remoteType === "video" && conferenceVideoFramesDecoded >= 0 && webrtcStatsData.video?.received?.totalFramesDecoded) {
                //calculate the delta between the last known value and the new one

                const delta = webrtcStatsData.video.received.totalFramesDecoded - conferenceVideoFramesDecoded;
                // data.video.framesDecodedDelta = delta;

                const framesDecodedDelta = localStorage.getItem('framesDecodedDelta') ? parseInt(localStorage.getItem('framesDecodedDelta'), 10) : 15;
                const framesDecodedDeltaOK = localStorage.getItem('framesDecodedDeltaOK') ? parseInt(localStorage.getItem('framesDecodedDeltaOK'), 10) : 4;
                const framesDecodedDeltaNOK = localStorage.getItem('framesDecodedDeltaNOK') ? parseInt(localStorage.getItem('framesDecodedDeltaNOK'), 10) : 1;
                if (delta >= 0 && ((delta / sec) <= framesDecodedDelta)) {
                    session.webrtcStats.video.videoPacketLostErrorCount += 1;
                    session.webrtcStats.video.videoPacketLostOKCount = 0;
                }
                else if ((delta / sec) >= 20) {
                    //no change
                    session.webrtcStats.video.videoPacketLostErrorCount = 0;
                    session.webrtcStats.video.videoPacketLostOKCount += 1;
                }

                //check if we need to update something
                if (session.webrtcStats.video.videoPacketLostOKCount >= framesDecodedDeltaOK) {
                    //good network conditions
                    session.webrtcStats.video.videoPacketLostOKCount = 0;
                    session.connection.jingle.rxSubject.next({ name: 'videoQualityChangeEvent', data: { decreaseQuality: false, sid: session.sid, confId: session.conference_id } });
                }

                if (session.webrtcStats.video.videoPacketLostErrorCount >= framesDecodedDeltaNOK) {
                    session.webrtcStats.video.videoPacketLostErrorCount = 0;
                    session.connection.jingle.rxSubject.next({ name: 'videoQualityChangeEvent', data: { decreaseQuality: true, sid: session.sid, confId: session.conference_id } });
                }
            }
        }

        //if we've audio, we need to send event update for the network flag
        //or also for the connectivity test
        //but ignore for Firefox
        if (window.adapter && window.adapter.browserDetails.browser === "chrome") {
            session.connection.jingle.rxSubject.next({ name: 'webrtcStatsEvent.jingle', data: { sid: session.sid, confId: session.conference_id, data: webrtcStatsData } });
        }
        this.logger.info("[WebrtcSessionStatsHandler] on webrtcStats for session " + session.sid + " : " + JSON.stringify(webrtcStatsData));
        if (webinarId) this.logger.db("info", "WebrtcSessionStatsHandler", "onWebrtcStatsForSession", { web: webinarId, sid: session.sid, confId: session.conference_id, data: webrtcStatsData });

        if ((webrtcStatsData.type === WebrtcStatisticsType.P2P) || (session.isInitiator && webrtcStatsData.type === WebrtcStatisticsType.CONFERENCE)) {
            this.webrtcConfigAndUtils.callsStats[session.sid] = metricsDataForServer;
            this.webrtcConfigAndUtils.callsStats[session.sid].id = id;
        }

        if (webrtcStatsData.type === WebrtcStatisticsType.WEBINAR) {
            //manage webinar cases
            if (!session.isInitiator && webrtcStatsData.mediaType === WebrtcStatisticsMediaType.AUDIO) {
                //check delta received packages
                if (webrtcStatsData.audio?.received?.deltaPacketsReceived === 0) {
                    //no packets received, check if we need to resubscribe to publisher
                    session.connection.jingle.rxSubject.next({ name: 'checkSubscriptionStatus', data: { sid: session.sid, confId: session.conference_id } });
                }
            }
        }

        //try to detect audio issues for P2P calls
        // if (webrtcStatsData.type === WebrtcStatisticsType.P2P) {
        //     if (inboundAudioStats.deltaPacketsReceived === 0) { session.webrtcStats.warningMessage = 'no audio packets received; '; }
        //     else if (jitterBufferEmittedCount === 0) { session.webrtcStats.warningMessage += 'jitter buffer is empty; local audio output issue detected; '; }
        //     else if (inboundAudioStats?.audioOutputLevel === 0) { session.webrtcStats.warningMessage += 'no distant party voice detected; '; }
        // }
        // if (session.webrtcStats.warningMessage) { this.logger.info(`[WebrtcSessionStatsHandler] on webrtcStats for session ${session.sid} -- warning: ${session.webrtcStats.warningMessage}`); }
    }

    /**
     *  Helper functions to ease the calculation for ranges
     */
    private prepareRangeForSsrc(ssrc: WebrtcStatisticsSsrc, rulesName: string, addDetails: boolean = false) {
        if (!ssrc.rules) { ssrc.rules = {}; }

        if (!ssrc.rules[rulesName]) {
            ssrc.rules[rulesName] = {
                value: 0,
                details: {},
                ranges: []
            };
        }

        //keep track of total values and index
        if (!ssrc[rulesName]) { ssrc[rulesName] = {}; }
        if (!ssrc[rulesName].totalCounter) { ssrc[rulesName].totalCounter = 0; }
        if (ssrc[rulesName].currentIndex === undefined) { ssrc[rulesName].currentIndex = -1; }
        if (!ssrc[rulesName].currentCounter) { ssrc[rulesName].currentCounter = 0; }

        if (addDetails) {
            if (!ssrc[rulesName].excellent) { ssrc[rulesName].excellent = 0; }
            if (!ssrc[rulesName].fair) { ssrc[rulesName].fair = 0; }
            if (!ssrc[rulesName].bad) { ssrc[rulesName].bad = 0; }
            if (!ssrc[rulesName].mediocre) { ssrc[rulesName].mediocre = 0; }
        }
    }

    private calculateRulesForSsrc(ssrc: WebrtcStatisticsSsrc, rulesName: string, highValue: number = 0, durationInSec: number = 1, withDetails: boolean = false) {
        if (highValue > 0) {
            ssrc[rulesName].totalCounter += 1;
            //check if we need to add new entry in the ranges array
            if (ssrc[rulesName].currentCounter === 0) {
                ssrc[rulesName].currentIndex += 1;
                ssrc.rules[rulesName].ranges.push({
                    start: new Date().toISOString(),
                    durationInSeconds: durationInSec,
                });
                ssrc[rulesName].currentCounter += 1;
            }
            //add values to the current entry
            else if (ssrc[rulesName].currentCounter > 0) {
                if (ssrc.rules[rulesName].ranges[ssrc[rulesName].currentIndex]) {
                    ssrc.rules[rulesName].ranges[ssrc[rulesName].currentIndex].durationInSeconds += durationInSec;
                }
            }
        }
        else {
            //reset the jitter counter
            ssrc[rulesName].currentCounter = 0;
        }

        //calculate duration % for all values
        ssrc.rules[rulesName].ranges.forEach((range) => {
            range.durationPercent = Math.round((range.durationInSeconds * 100 / (ssrc.measures * durationInSec)) * 100) / 100;
        });
        if (ssrc[rulesName].totalCounter > 0) { ssrc.rules[rulesName].value = Math.round((ssrc[rulesName].totalCounter * 100 / ssrc.measures) * 100) / 100; }

        if (withDetails) {
            ssrc.rules[rulesName].details = {
                percentExcellent: ssrc[rulesName].excellent ? Math.round((ssrc[rulesName].excellent * 100 / ssrc.measures) * 100) / 100 : 0,
                percentFair: ssrc[rulesName].fair ? Math.round((ssrc[rulesName].fair * 100 / ssrc.measures) * 100) / 100 : 0,
                percentBad: ssrc[rulesName].bad ? Math.round((ssrc[rulesName].bad * 100 / ssrc.measures) * 100) / 100 : 0,
                percentMediocre: ssrc[rulesName].mediocre ? Math.round((ssrc[rulesName].mediocre * 100 / ssrc.measures) * 100) / 100 : 0
            };

            if (ssrc.rules[rulesName].ranges?.length) {
                ssrc.rules[rulesName].details.firstOccurrence = ssrc.rules[rulesName].ranges[0].start;
            }

            ssrc.rules[rulesName].value = ssrc.rules[rulesName].details.percentMediocre;
        }

    }

    /**
     * High Jitter (inbound / outbound audio ssrc)
     * Rule Computation
        When the Jitter exceeds this threshold, it often leads to noticeable degraded call quality—resulting in choppy audio or speech that seems to speed up or slow down from the user’s perspective.

        For every Jitter value, count how many are:

        Excellent: JITTER < SETTINGS_JITTER_FAIR
        Fair: SETTINGS_JITTER_FAIR < JITTER <= SETTINGS_JITTER_BAD
        Bad: SETTINGS_JITTER_BAD < JITTER <= SETTINGS_JITTER_MEDIOCRE
        Mediocre: JITTER > SETTINGS_JITTER_MEDIOCRE
     */
    private calculateHighJitterForSsrc(ssrc: WebrtcStatisticsSsrc, session) {
        if (ssrc.kind !== "audio") return;

        const rulesName = "percentHighJitter";

        this.prepareRangeForSsrc(ssrc, rulesName, true);

        let isMediocre = 0;

        if (ssrc.jitterMs < this.metricsValues.jitterFair) { ssrc[rulesName].excellent += 1; }
        else if (ssrc.jitterMs <= this.metricsValues.jitterBad) { ssrc[rulesName].fair += 1; }
        else if (ssrc.jitterMs <= this.metricsValues.jitterMediocre) { ssrc[rulesName].bad += 1; }
        else { ssrc[rulesName].mediocre += 1; isMediocre = 1; }

        this.calculateRulesForSsrc(ssrc, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, true);
    }

    /**
     *
     * When the RTT exceeds this threshold, it often leads to noticeable latency and degraded call quality—resulting in choppy audio or speech that seems to speed up or slow down from the user’s perspective.

     * Ranges details
        The ranges property stores the list of all mediocre periods. A period corresponds to a list of successive values that have incremented the counter.

        Each period is a JSON structure containing the following properties:

        start: It corresponds to the timestamp of the first element from a period (timestamp of the report)
        durationPercent: It corresponds to the duration of the successive measures that have increased the counter - In percentage of the call
        durationInSeconds (alternative or additional property): It corresponds to the duration of the successive measures that have increased the counter – In seconds
        If no current period and a new value increments the counter --> create a new period (timestamp of new value, durationInSeconds= interval)
        If there is a current period and a new value increments the counter --> increment the duration (durationInSeconds + interval)
        If there is a current period and a new value doesn't increment the counter --> store the current period and reset it
        if there is no current period and a new value doesn't increment the counter --> do nothing
     */
    private calculateRangeRttForSsrc(ssrc: WebrtcStatisticsSsrc, inboundAudioStats: WebrtcStatisticsInboundAudio, session) {
        if (!inboundAudioStats) { inboundAudioStats = { roundTripTimeMs: 100 }; }
        if (!inboundAudioStats.roundTripTimeMs) { inboundAudioStats.roundTripTimeMs = 100; }

        const rulesName = "percentHighRTT";

        this.prepareRangeForSsrc(ssrc, rulesName, true);

        // When parsing stats
        let isMediocre = 0;
        if (inboundAudioStats.roundTripTimeMs < this.metricsValues.RTTFair) { ssrc[rulesName].excellent += 1; }
        else if (inboundAudioStats.roundTripTimeMs <= this.metricsValues.RTTBad) { ssrc[rulesName].fair += 1; }
        else if (inboundAudioStats.roundTripTimeMs <= this.metricsValues.RTTMediocre) { ssrc[rulesName].bad += 1; }
        else { ssrc[rulesName].mediocre += 1; isMediocre = 1; }

        this.calculateRulesForSsrc(ssrc, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, true);
    }

    /**
     * Rule Computation
        When packet loss surpasses this threshold, it often causes noticeable audio degradation, leading to choppy or distorted sound as missing audio frames are concealed or replaced with synthetic sound.

        For every packetLoss value (mean during the interval, as computed for the MOS), count how many are:
        Excellent: packetLoss = 0
        Fair: 0 < packetLoss <= SETTINGS_PACKETLOSS_BAD
        Bad: SETTINGS_PACKETLOSS_BAD < packetLoss <= SETTINGS_PACKETLOSS_MEDIOCRE
        Mediocre: RTT > SETTINGS_PACKETLOSS_MEDIOCRE

        Ranges details
        The ranges property stores the list of all mediocre periods. A period corresponds to a list of successive values that have incremented the counter.
     */
    private calculateHighPacketLossForSsrc(ssrc: WebrtcStatisticsSsrc, session) {

        const rulesName = "percentHighLoss";

        this.prepareRangeForSsrc(ssrc, rulesName, true);

        // When parsing stats
        let isMediocre = 0;
        if (ssrc.deltaPacketsLost === 0) { ssrc[rulesName].excellent += 1; }
        else if (ssrc.deltaPacketsLost <= this.metricsValues.packetLossBad) { ssrc[rulesName].fair += 1; }
        else if (ssrc.deltaPacketsLost <= this.metricsValues.packetLossMediocre) { ssrc[rulesName].bad += 1; }
        else { ssrc[rulesName].mediocre += 1; isMediocre = 1; }

        this.calculateRulesForSsrc(ssrc, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, true);
    }

    /**
     *
     *
     * Audio packets are temporarily stored in a jitter buffer, where they are collected, reordered, and assembled into continuous audio samples.
        The jitter buffer compensates for variations in network latency—such as delay or packet arrival times—ensuring the audio stream remains smooth and uninterrupted for the listener.
        Sometimes packets seem to stay a long time in the buffer, whereas the network seems stable and without variation. This is called bufferbloat or high dejitter.
        Bufferbloat can explain choppy audio or interrupted audio playback.

        Ranges details
        The ranges property stores the list of all mediocre periods. A period corresponds to a list of successive values that have incremented the counter.
        Each period is a JSON structure containing the following properties:
        start: It corresponds to the timestamp of the first element from a period (timestamp of the report)
        durationPercent: It corresponds to the duration of the successive measures that have increased the counter - In percentage of the call
        durationInSeconds (alternative or additional property): It corresponds to the duration of the successive measures that have increased the counter – In seconds
        If no current period and a new value increments the counter --> create a new period (timestamp of new value, durationInSeconds= interval)
        If there is a current period and a new value increments the counter --> increment the duration (durationInSeconds + interval)
        If there is a current period and a new value doesn't increment the counter --> store the current period and reset it
        if there is no current period and a new value doesn't increment the counter --> do nothing
     */
    private calculateRangeHighDejitterForSsrc(ssrc: WebrtcStatisticsSsrc, inboundAudioStats: WebrtcStatisticsInboundAudio, session) {

        if (!inboundAudioStats.mos) return;

        const rulesName = "percentHighDejitter";

        this.prepareRangeForSsrc(ssrc, rulesName, false);

        // When parsing stats
        const isMediocre = (inboundAudioStats.jitterBufferMs - inboundAudioStats.roundTripTimeMs / 2 > this.metricsValues.bufferBloatThreshold && inboundAudioStats.roundTripTimeMs <= this.metricsValues.RTTBad) ? 1 : 0;

        this.calculateRulesForSsrc(ssrc, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);

    }

    private calculateMosValuesForSsrc(ssrc: WebrtcStatisticsSsrc, inboundAudioStats: WebrtcStatisticsInboundAudio, session) {
        if (!ssrc.rules) { ssrc.rules = {}; }
        if (!inboundAudioStats.mos) return;

        if (!ssrc.audioStats) { ssrc.audioStats = {}; }
        if (!ssrc.audioStats.mosVeryHigh) { ssrc.audioStats.mosVeryHigh = 0; }
        if (!ssrc.audioStats.mosHigh) { ssrc.audioStats.mosHigh = 0; }
        if (!ssrc.audioStats.mosMedium) { ssrc.audioStats.mosMedium = 0; }
        if (!ssrc.audioStats.mosLow) { ssrc.audioStats.mosLow = 0; }
        if (!ssrc.audioStats.mosMediocre) { ssrc.audioStats.mosMediocre = 0; }

        if (inboundAudioStats.mos >= this.metricsValues.MOSVeryHigh) { ssrc.audioStats.mosVeryHigh += 1; }
        else if (inboundAudioStats.mos >= this.metricsValues.MOSHigh) { ssrc.audioStats.mosHigh += 1; }
        else if (inboundAudioStats.mos >= this.metricsValues.MOSMedium) { ssrc.audioStats.mosMedium += 1; }
        else if (inboundAudioStats.mos > this.metricsValues.MOSMediocre) { ssrc.audioStats.mosLow += 1; }
        else { ssrc.audioStats.mosMediocre += 1; }

        //calculate % for the duration
        ssrc.mos = {
            percentVeryHigh: ssrc.audioStats.mosVeryHigh ? Math.round((ssrc.audioStats.mosVeryHigh * 100 / session.webrtcStats.iteration) * 100) / 100 : 0,
            percentHigh: ssrc.audioStats.mosHigh ? Math.round((ssrc.audioStats.mosHigh * 100 / session.webrtcStats.iteration) * 100) / 100 : 0,
            percentMedium: ssrc.audioStats.mosMedium ? Math.round((ssrc.audioStats.mosMedium * 100 / session.webrtcStats.iteration) * 100) / 100 : 0,
            percentLow: ssrc.audioStats.mosLow ? Math.round((ssrc.audioStats.mosLow * 100 / session.webrtcStats.iteration) * 100) / 100 : 0,
            percentMediocre: ssrc.audioStats.mosMediocre ? Math.round((ssrc.audioStats.mosMediocre * 100 / session.webrtcStats.iteration) * 100) / 100 : 0,
            mean: inboundAudioStats.meanMOS
        } as MosLevelStats;
    }

    private calculateLateAudioValuesForSSRC(report, session, ssrcId) {
        const rulesName = "percentLateAudio";
        const stats = session.webrtcStats.ssrc[ssrcId];

        this.prepareRangeForSsrc(stats, rulesName, false);

        const totalPacketsDiscarded = report.packetsDiscarded;

        // Compute delta
        const deltaPacketsDiscarded = totalPacketsDiscarded - stats.audioStats.oldPacketsDiscarded; // Diff with the previous measure

        //calculate % for delta period
        const lateAudioPercent = deltaPacketsDiscarded > 0 ? 1 : 0;
        let isMediocre = 0;
        if (lateAudioPercent) { isMediocre = 1; }

        stats.audioStats.oldPacketsDiscarded = totalPacketsDiscarded;

        this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
    }

    private calculateHighSendAudioDelay(report, session, ssrcId) {
        const rulesName = "percentSentDelayed";
        const stats = session.webrtcStats.ssrc[ssrcId];

        this.prepareRangeForSsrc(stats, rulesName, false);

        const totalPacketSendDelay = report.totalPacketSendDelay;

        // Compute delta in seconds
        const deltaPacketSendDelay = totalPacketSendDelay - stats.audioStats.oldTotalPacketSendDelay; // Diff with the previous measure

        //calculate % for delta period
        const packetSendDelayPercent = Math.round(deltaPacketSendDelay * 1000) > this.metricsValues.packetsSendDelayThreshold ? 1 : 0;
        let isMediocre = 0;
        if (packetSendDelayPercent) { isMediocre = 1; }

        stats.audioStats.oldTotalPacketSendDelay = totalPacketSendDelay;

        this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
    }

    private calculateHighConcealmentValuesForSSRC(report, session, ssrcId) {

        const rulesName = "percentHighConcealment";
        const stats = session.webrtcStats.ssrc[ssrcId];

        this.prepareRangeForSsrc(stats, rulesName, false);

        const totalSamplesReceived = report.totalSamplesDuration;
        const concealedSamples = report.concealedSamples;

        // Compute delta
        const deltaTotalSamplesReceived = totalSamplesReceived - stats.audioStats.oldTotalConcealedSamples; // Diff with the previous measure
        const deltaSamplesReceived = concealedSamples - stats.audioStats.oldConcealedSamples; // Diff with the previous measure

        //calculate % for delta period
        const concealedSamplesPercent = deltaTotalSamplesReceived > 0 ? deltaSamplesReceived / deltaTotalSamplesReceived * 100 : 0;
        let isMediocre = 0;
        if (concealedSamplesPercent > this.metricsValues.concealmentThreshold) { isMediocre = 1; }

        stats.audioStats.oldTotalConcealedSamples = totalSamplesReceived;
        stats.audioStats.oldConcealedSamples = concealedSamples;

        this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
    }

    private calculateNoPackets(report, session, ssrcId) {
        const rulesName = "percentNoPacket";
        const stats = session.webrtcStats.ssrc[ssrcId];

        this.prepareRangeForSsrc(stats, rulesName, false);

        let deltaPackets = 0;
        //calculate delta packets
        if (report.type === "inbound-rtp") {
            deltaPackets = report.packetsReceived - stats.deltaPackets;
            if (deltaPackets < 0) deltaPackets = 0;
            stats.deltaPackets = report.packetsReceived;
        }
        else if (report.type === "outbound-rtp") {
            deltaPackets = report.packetsSent - stats.deltaPackets;
            if (deltaPackets < 0) deltaPackets = 0;
            stats.deltaPackets = report.packetsSent;
        }

        const isMediocre = deltaPackets ? 0 : 1;
        this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
    }

    private calculateHighSpeakerDelayForSSRC(report, session, ssrcId) {

        const rulesName = "percentHighSpeakerDelay";
        const stats = session.webrtcStats.ssrc[ssrcId];

        this.prepareRangeForSsrc(stats, rulesName, false);

        const totalSamplesReceived = report.totalSamplesCount;
        const totalPlayoutDelay = report.totalPlayoutDelay;

        // Compute delta
        const deltaTotalSamplesReceived = totalSamplesReceived - stats.audioStats.oldTotalSamplesCount; // Diff with the previous measure
        const deltaPlayoutDelay = totalPlayoutDelay - stats.audioStats.oldTotalPlayoutDelay; // Diff with the previous measure

        //calculate % for delta period
        const deltaPLayoutPercent = deltaTotalSamplesReceived > 0 ? deltaPlayoutDelay / deltaTotalSamplesReceived * 100 : 0;
        let isMediocre = 0;
        if (deltaPLayoutPercent > this.metricsValues.speakerDelayThreshold) { isMediocre = 1; }

        stats.audioStats.oldTotalSamplesCount = totalSamplesReceived;
        stats.audioStats.oldTotalPlayoutDelay = totalPlayoutDelay;

        this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
    }

    private calculateNoAudio(report, session, ssrcId, outboundAudioStats?) {
        const rulesName = "percentNoAudio";
        const stats = session.webrtcStats.ssrc[ssrcId];

        this.prepareRangeForSsrc(stats, rulesName, false);

        //check if this is inboud or outbound report
        if (report.type === "inbound-rtp") {
            //get current value for bytesReceived
            const bytesReceived = session.webrtcStats?.audio?.bytesReceived ? session.webrtcStats?.audio?.bytesReceived : 0;
            const totalAudioEnergy = report.totalAudioEnergy;

            // Compute delta
            const deltaAudioEnergy = totalAudioEnergy - stats.audioStats.oldTotalAudioEnergy; // Diff with the previous measure
            const deltaBytesReceived = report.bytesReceived - bytesReceived; // Diff with the previous measure

            let isMediocre = 0;

            // When parsing stats, compute the percent of no audio
            isMediocre += (deltaBytesReceived > 0 && deltaAudioEnergy === 0) ? 1 : 0;

            this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
        }
        //outbound rtp
        else if (report.type === "media-source") {
            //get current value for bytes sent
            const bytesSent = outboundAudioStats?.deltaAudioKBSent ? outboundAudioStats.deltaAudioKBSent : 0;
            const totalAudioEnergy = report.totalAudioEnergy;

            // Compute delta
            const deltaAudioEnergy = totalAudioEnergy - stats.audioStats.oldTotalAudioEnergy; // Diff with the previous measure

            let isMediocre = 0;

            // When parsing stats, compute the percent of no audio
            isMediocre += (bytesSent > 0 && deltaAudioEnergy === 0) ? 1 : 0;

            this.calculateRulesForSsrc(stats, rulesName, isMediocre, session.webrtcStats.intervalDuration / 1000, false);
        }
    }

    private calculateAudioLevelValuesForSSRC(report, session, ssrcId) {
        const instantAudioLevel = report.audioLevel;
        const totalAudioEnergy = report.totalAudioEnergy;
        const totalSamplesDuration = report.totalSamplesDuration;

        const stats = session.webrtcStats.ssrc[ssrcId];
        // Compute delta
        let deltaAudioEnergy = totalAudioEnergy - stats.audioStats.oldTotalAudioEnergy; // Diff with the previous measure
        let deltaSamplesDuration = totalSamplesDuration - stats.audioStats.oldTotalSamplesDuration; // Diff with the previous measure

        let avgAudioLevel = 0;
        if (deltaAudioEnergy < 0) deltaAudioEnergy = 0;
        if (deltaSamplesDuration < 0) deltaSamplesDuration = 0;
        if (deltaAudioEnergy && deltaSamplesDuration) {
            avgAudioLevel = Math.sqrt(deltaAudioEnergy / deltaSamplesDuration);
        }

        //save value to show in logs later
        session.webrtcStats.audio.rms = avgAudioLevel;

        stats.audioStats.oldTotalAudioEnergy = totalAudioEnergy;
        stats.audioStats.oldTotalSamplesDuration = totalSamplesDuration;

        // Get the highest value between the agerage value over the interval and the "instant" value
        let audioLevel = Math.max(instantAudioLevel, avgAudioLevel);
        if (!audioLevel) audioLevel = 0;

        //add audio level to values
        if (audioLevel === 0) { stats.audioStats.muted += 1; }
        else if (audioLevel > 0 && audioLevel <= this.metricsValues.audioLevelBackground) { stats.audioStats.quiet += 1; }
        else if (audioLevel > this.metricsValues.audioLevelBackground && audioLevel <= this.metricsValues.audioLevelActive) { stats.audioStats.background += 1; }
        else if (audioLevel > this.metricsValues.audioLevelActive && audioLevel <= this.metricsValues.audioLevelLoud) { stats.audioStats.active += 1; }
        else if (audioLevel > this.metricsValues.audioLevelLoud) { stats.audioStats.loud += 1; }

        //calculate % for the duration
        stats.audioLevel = {
            percentMuted: stats.audioStats.muted ? Math.round((stats.audioStats.muted * 100 / stats.measures) * 100) / 100 : 0,
            percentQuiet: stats.audioStats.quiet ? Math.round((stats.audioStats.quiet * 100 / stats.measures) * 100) / 100 : 0,
            percentBackground: stats.audioStats.background ? Math.round((stats.audioStats.background * 100 / stats.measures) * 100) / 100 : 0,
            percentActive: stats.audioStats.active ? Math.round((stats.audioStats.active * 100 / stats.measures) * 100) / 100 : 0,
            percentLoud: stats.audioStats.loud ? Math.round((stats.audioStats.loud * 100 / stats.measures) * 100) / 100 : 0,
        } as AudioLevelStats;
    }
}
